import { createStyles } from '@mantine/emotion';
import { useMediaQuery } from '@mantine/hooks';
import { PageHeader } from '../../components/PageHeader';
import { Container, Divider, Title, Text, Stack, Grid, List, Group, Anchor } from '@mantine/core';
import { t } from 'ttag';
import { PageHero } from '../../components/PageHero';

const useStyles = createStyles((theme) => ({
  content: {
    width: '100%',
    height: '100%',
    '> #section:nth-of-type(odd)': {
      backgroundColor: 'red',
    },
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.gray[0],
  },
  policy: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'flex-start',
  },
}));

const WAVE_HEIGHT = '98px';

export const PrivacyPolicy = () => {
  const { classes, theme } = useStyles();

  const smallScreen = useMediaQuery('(max-width: 992px)');
  const CONTAINER_PADDING = smallScreen ? '20px' : '50px';

  const waveColors = ['#d6eefc33', '#d6eefc99', '#d6eefccc', theme.colors.gray[0]];

  return (
    <div className={classes.content}>
      <PageHeader links={[]} />
      <PageHero waveColors={waveColors} waveHeight={WAVE_HEIGHT} />
      <Container className={classes.policy} size="md" p={CONTAINER_PADDING}>
        <Title order={2}>{t`Integritetspolicy`}</Title>
        <Divider mt={'sm'} mb={'xl'} />
        <Stack>
          <Title order={3}>{t`Gällande från 1 juli 2022 (uppdaterad version)`}</Title>
          <Text>{t`Denna integritetspolicy beskriver hur Grantme AB (“Grantme”, “vi”, “oss”) behandlar personuppgifter när du använder våra tjänster.`}</Text>
          <Text>{t`För att kunna tillhandahålla vår tjänst och kommunicera med dig behöver vi behandla vissa personuppgifter, såsom namn, personnummer, kontaktuppgifter samt uppgifter kopplade till dina uppdrag eller ansökningar.`}</Text>

          <Title order={3}>{t`1. Vilka personuppgifter vi behandlar och varför`}</Title>
          <Text>{t`Vi behandlar endast de personuppgifter som är nödvändiga för att kunna tillhandahålla våra tjänster och fullgöra vårt avtal med dig. Nedan framgår vilka uppgifter vi behandlar, varifrån de kommer och för vilket ändamål.`}</Text>

          <Grid>
            <Grid.Col span={4}>
              <Text fw={600}>{t`Personuppgift`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text fw={600}>{t`Källa`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text fw={600}>{t`Ändamål`}</Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text>{t`Personnummer`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`Användaren själv`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`För att säkert identifiera dig, inklusive vid inloggning och digital signering.`}</Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text>{t`Namn`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`Användaren själv`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`För att Grantme och andra användare du samverkar med ska kunna identifiera dig.`}</Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text>{t`E-postadress`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`Användaren själv`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`För att skicka notiser, information, bekräftelser och tjänsterelaterad kommunikation.`}</Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text>{t`Mobiltelefonnummer`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`Användaren själv`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`För tjänsterelaterad kommunikation och säkerhetsrelaterade funktioner (ex. tvåfaktorsverifiering).`}</Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text>{t`IP-adress`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`Genereras vid användning av tjänsten`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`För logghantering, felsökning, säkerhetsskydd, intrångsdetektering samt utveckling av säkerhetsrelaterade funktioner.`}</Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text>{t`Uppdrags- och bolagsinformation`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`Offentliga källor (t.ex. Bolagsverket`}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text>{t`För att möjliggöra hantering av uppdrag, ansökningar och fullmakter inom ramen för våra tjänster.`}</Text>
            </Grid.Col>
          </Grid>

          <Text>{t`Den rättsliga grunden för vår behandling är fullgörande av avtal samt, i vissa fall, vårt berättigade intresse av att upprätthålla säkerhet och teknisk funktionalitet.`}</Text>

          <Title order={3}>{t`2. Hur vi skyddar dina personuppgifter`}</Title>
          <Text>{t`Grantme vidtar lämpliga tekniska och organisatoriska säkerhetsåtgärder för att skydda dina personuppgifter mot obehörig åtkomst, förlust, missbruk eller oavsiktlig spridning. Endast personer som behöver tillgång till uppgifterna för att utföra sina arbetsuppgifter får tillgång till dem.`}</Text>
          <Text>{t`Vi säljer aldrig personuppgifter och delar dem endast med tredje parter som är nödvändiga för att tillhandahålla tjänsten (exempelvis tekniska driftleverantörer). Dessa agerar som personuppgiftsbiträden i enlighet med biträdesavtal som uppfyller kraven enligt GDPR.`}</Text>

          <Title order={3}>{t`3. Var behandlingen sker`}</Title>
          <Text>{t`All behandling av personuppgifter som Grantme ansvarar för sker inom EU/EES. Ingen överföring till tredje land förekommer.`}</Text>

          <Title order={3}>{t`4. Hur länge vi sparar personuppgifter`}</Title>
          <Text>{t`Vi behandlar dina personuppgifter så länge du använder vår tjänst och sparar dem därefter endast så länge det är nödvändigt för att:`}</Text>

          <List>
            <List.Item>
              <Text>{t`uppfylla avtal eller rättsliga skyldigheter, eller`}</Text>
            </List.Item>
            <List.Item>
              <Text>{t`säkerställa spårbarhet i systemet under en begränsad tid efter avslutat konto.`}</Text>
            </List.Item>
          </List>

          <Text>{t`Uppgifter som inte längre behövs raderas eller anonymiseras.`}</Text>

          <Title order={3}>{t`5. Dina rättigheter`}</Title>
          <Text>{t`Du har rätt att:`}</Text>

          <List>
            <List.Item>
              <Text>{t`begära tillgång till de personuppgifter vi behandlar om dig`}</Text>
            </List.Item>
            <List.Item>
              <Text>{t`få felaktiga uppgifter rättade`}</Text>
            </List.Item>
            <List.Item>
              <Text>{t`begära radering av dina personuppgifter`}</Text>
            </List.Item>
            <List.Item>
              <Text>{t`begära begränsning av behandlingen`}</Text>
            </List.Item>
            <List.Item>
              <Text>{t`invända mot behandling som sker med stöd av berättigat intresse`}</Text>
            </List.Item>
            <List.Item>
              <Text>{t`få ut dina personuppgifter i ett strukturerat och maskinläsbart format (dataportabilitet)`}</Text>
            </List.Item>
          </List>

          <Text>{t`Om du har lämnat samtycke för viss behandling har du rätt att när som helst återkalla detta.`}</Text>
          <Text>{t`Du har även rätt att inge klagomål till Integritetsskyddsmyndigheten (IMY) om du anser att dina uppgifter behandlas i strid med GDPR.`}</Text>

          <Title order={3}>{t`6. Personuppgiftsansvarig och kontakt`}</Title>
          <Stack gap={0}>
            <Text>{t`Personuppgiftsansvarig:`}</Text>
            <Text fw={600}>Grantme AB</Text>
            <Group gap={4}>
              <Text>{t`E-post:`}</Text>
              <Anchor href="mailto:support@grantme.se">support@grantme.se</Anchor>
            </Group>
          </Stack>
          <Text>{t`Kontakta oss om du vill utöva någon av dina rättigheter eller har frågor om vår behandling av personuppgifter.`}</Text>
        </Stack>
      </Container>
    </div>
  );
};
